import { Chip, CircularProgress, Link, Switch, Typography, capitalize } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useUpdateImpactGroupTemplateMutation } from '../../graphql/generated/mutations.gql.generated';
import type { AllImpactGroupsQuery } from '../../graphql/generated/queries.gql.generated';

type ImpactGroupRow = NonNullable<
  NonNullable<AllImpactGroupsQuery['impactGroupTemplates']>['groups'][number]
>;

const ActiveToggleCell = ({ rowId, active }: { rowId: number; active: boolean }) => {
  const [updateMutation] = useUpdateImpactGroupTemplateMutation();
  const [isLoading, setIsLoading] = useState(false);

  // Track pending optimistic state separately from server state
  const [optimisticActive, setOptimisticActive] = useState<boolean | null>(null);

  // Use ref to prevent stale closure issues
  const isLoadingRef = useRef(false);

  // The displayed value: optimistic state if pending, otherwise server state
  const displayActive = optimisticActive !== null ? optimisticActive : active;

  // Sync optimistic state with server state when server catches up
  useEffect(() => {
    if (optimisticActive !== null && optimisticActive === active) {
      setOptimisticActive(null);
    }
  }, [active, optimisticActive]);

  const handleToggle = useCallback(() => {
    // Prevent double-clicks while loading
    if (isLoadingRef.current) return;

    const nextActive = !displayActive;

    setOptimisticActive(nextActive);
    setIsLoading(true);
    isLoadingRef.current = true;

    updateMutation({
      variables: {
        rowId,
        patch: { active: nextActive },
      },
    })
      .then(() => {
        setIsLoading(false);
        isLoadingRef.current = false;
      })
      .catch((err) => {
        console.error('Failed to update:', err);
        setOptimisticActive(null);
        setIsLoading(false);
        isLoadingRef.current = false;
      });
  }, [displayActive, rowId, updateMutation]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Switch
        checked={displayActive}
        onChange={handleToggle}
        size="small"
        color="primary"
        disabled={isLoading}
      />
      {isLoading && <CircularProgress size={16} />}
    </div>
  );
};

export const useImpactGroupsTableColumns = () => {
  return useMemo<MRT_ColumnDef<ImpactGroupRow>[]>(
    () => [
      {
        id: 'rowId',
        header: 'ID',
        accessorKey: 'rowId',
      },
      {
        header: 'Active',
        accessorKey: 'active',
        muiTableHeadCellProps: { sx: { minWidth: 120 } },
        muiTableBodyCellProps: { sx: { minWidth: 120 } },
        Cell: ({ row }) => (
          <ActiveToggleCell
            key={row.original.rowId}
            rowId={row.original.rowId}
            active={row.original.active ?? false}
          />
        ),
      },
      {
        header: 'Label',
        accessorKey: 'label',
        muiTableHeadCellProps: { sx: { minWidth: 180 } },
        muiTableBodyCellProps: { sx: { minWidth: 180 } },
      },
      {
        header: 'Slug',
        accessorKey: 'slug',
        muiTableHeadCellProps: { sx: { minWidth: 160 } },
        muiTableBodyCellProps: { sx: { minWidth: 160 } },
        isPinned: true,
        Cell: ({ row }) => {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'action.hover',
                display: 'inline-block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {row.original.slug}
            </Typography>
          );
        },
      },
      {
        header: 'Scope',
        accessorKey: 'scope',
        muiTableHeadCellProps: { sx: { minWidth: 140 } },
        muiTableBodyCellProps: { sx: { minWidth: 140 } },
        Cell: ({ row }) => {
          return (
            <Chip
              size="small"
              label={capitalize(row.original.scope)}
              color={row.original.scope === 'account' ? 'primary' : 'secondary'}
            />
          );
        },
      },
      {
        header: 'Notification Message',
        accessorKey: 'notificationMessage',
        muiTableHeadCellProps: { sx: { minWidth: 220 } },
        muiTableBodyCellProps: { sx: { minWidth: 220 } },
      },
      {
        header: 'Severity',
        accessorKey: 'severity',
        muiTableHeadCellProps: { sx: { minWidth: 120 } },
        muiTableBodyCellProps: { sx: { minWidth: 120 } },
        Cell: ({ row }) => {
          const severity = row.original.severity;
          const color =
            severity === 'critical' ? 'error' : severity === 'warning' ? 'warning' : 'info';
          return (
            <Chip size="small" label={capitalize(severity)} color={color as any} variant="light" />
          );
        },
      },
      {
        header: 'Action URL',
        accessorKey: 'actionUrl',
        muiTableHeadCellProps: { sx: { minWidth: 200 } },
        muiTableBodyCellProps: { sx: { minWidth: 200 } },

        Cell: ({ row }) => {
          const url = row.original.actionUrl as string | null | undefined;
          if (!url)
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          return (
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: 'inline-block',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </Link>
          );
        },
      },
      {
        header: 'Description',
        accessorKey: 'description',
        muiTableHeadCellProps: { sx: { minWidth: 260 } },
        muiTableBodyCellProps: { sx: { minWidth: 260 } },
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        muiTableHeadCellProps: { sx: { minWidth: 160 } },
        muiTableBodyCellProps: { sx: { minWidth: 160 } },
      },
      {
        header: 'Updated At',
        accessorKey: 'updatedAt',
        muiTableHeadCellProps: { sx: { minWidth: 160 } },
        muiTableBodyCellProps: { sx: { minWidth: 160 } },
      },
      {
        header: 'Updated By',
        accessorKey: 'updatedBy',
        muiTableHeadCellProps: { sx: { minWidth: 180 } },
        muiTableBodyCellProps: { sx: { minWidth: 180 } },
      },
      {
        header: 'Template Expires At',
        accessorKey: 'templateExpiresAt',
        muiTableHeadCellProps: { sx: { minWidth: 180 } },
        muiTableBodyCellProps: { sx: { minWidth: 180 } },
      },
    ],
    [],
  );
};
